import { Injectable, Signal, computed, signal } from '@angular/core';
import type { UnifiedTask, TaskCard, TaskStatus } from '../models/task-management.model';
import type { ServiceProvider } from '../../../../shared/definitions/service-provider';
import type { Repair } from '../../../../shared/definitions/repair';
import type { ITaskManagementStore } from './task-management-store.interface';
import { interventionPhaseFromRepair } from '../../repairs/repair-detail/services/intervention-phase.util';

@Injectable({ providedIn: 'root' })
export class TaskManagementMockStore implements ITaskManagementStore {
  private readonly _tasks = signal<readonly UnifiedTask[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly tasks = this._tasks.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._tasks().length);

  readonly byStatus: Signal<Readonly<Record<TaskStatus, readonly UnifiedTask[]>>> = computed(() => {
    const buckets: Record<string, UnifiedTask[]> = {};
    for (const t of this._tasks()) {
      (buckets[t.status] ??= []).push(t);
    }
    return buckets as unknown as Readonly<Record<TaskStatus, readonly UnifiedTask[]>>;
  });

  async refresh(): Promise<void> {
    this._loading.set(true);
    await Promise.resolve();
    this._loading.set(false);
  }

  getTasksForProperty(propertyUID: string): readonly UnifiedTask[] {
    return this._tasks().filter(t => t.propertyUID === propertyUID);
  }

  getTasksByStatus(status: TaskStatus): readonly UnifiedTask[] {
    return this._tasks().filter(t => t.status === status);
  }

  getTaskById(taskId: string): UnifiedTask | null {
    return this._tasks().find(t => t.id === taskId) ?? null;
  }

  toKanbanCards(tasks?: readonly UnifiedTask[]): TaskCard[] {
    const source = tasks ?? this._tasks();
    return source.map(t => ({
      id: t.id,
      title: t.title,
      description: t.description,
      status: t.status,
      priority: t.priority,
      propertyUID: t.propertyUID,
      propertyName: t.propertyName,
      assignedTo: t.assignedTo,
      assignedProvider: t.assignedProvider,
      assignedProviderName: t.assignedProviderName,
      dueDate: t.dueDate,
      scheduledDate: t.scheduledDate,
      progress: t.progress,
      attachments: t.attachments,
      cost: t.cost,
      estimatedCost: t.estimatedCost,
      actualCost: t.actualCost,
      type: t.type,
      specialties: t.specialties,
      phase:
        t.type === 'repair'
          ? interventionPhaseFromRepair(t.originalData as Repair)
          : undefined
    } as unknown as TaskCard));
  }

  async updateTaskStatus(taskId: string, newStatus: TaskStatus): Promise<void> {
    this._tasks.set(
      this._tasks().map(t => (t.id === taskId ? { ...t, status: newStatus, updatedAt: new Date() } : t))
    );
  }

  async assignServiceProvider(taskId: string, provider: ServiceProvider): Promise<void> {
    this._tasks.set(
      this._tasks().map(t =>
        t.id === taskId
          ? { ...t, assignedProvider: provider, assignedDate: new Date(), updatedAt: new Date() }
          : t
      )
    );
  }

  async updateTaskSchedule(taskId: string, scheduledDate: Date): Promise<void> {
    this._tasks.set(
      this._tasks().map(t =>
        t.id === taskId ? { ...t, scheduledDate, dueDate: scheduledDate, updatedAt: new Date() } : t
      )
    );
  }

  async updateTaskCompletionData(
    taskId: string,
    completionData: {
      completionNotes?: string;
      rating?: number;
      costActual?: number;
      durationActual?: number;
      issues?: string[];
      recommendations?: string;
    }
  ): Promise<void> {
    this._tasks.set(
      this._tasks().map(t =>
        t.id === taskId ? { ...t, ...completionData, updatedAt: new Date() } : t
      )
    );
  }

  /** Test helper — seed the mock store with a task list. */
  _seedForTests(tasks: readonly UnifiedTask[]): void {
    this._tasks.set(tasks);
  }
}
